/**
 * One planned drop on the Day timeline (19/10/2026 — "which list is being
 * used"): Time (Danish first) · # · List (the group's chip when several,
 * the list's name always) · Notes (clamps, pins, optimizer, runtime state,
 * the slot controls on hover) · Gateway · Senders · Texts (22/10/2026 — "the
 * full picture") · Amount · $ / SMS (the route's price, 23/10/2026 — client
 * QA: "Include route price based on gateway/route") · Est. $. Warn-tinted
 * when the row carries a clamp or was skipped.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { usd } from '@/lib/reporting-currency';

import { cn } from '@/lib/utils';

import type { DayPlanContainerRow, DayPlanDropRow } from '../../drop-generator.types';
import { GatewayCell } from './row/GatewayCell';
import { ListCell } from './row/ListCell';
import { SendersCell } from './row/SendersCell';
import { SlotActions } from './row/SlotActions';
import { TextsCell } from './row/TextsCell';
import { TimeCell } from './row/TimeCell';
import { spineColor, TIMELINE_GRID } from './timeline-grid';
import { TimelineNote } from './TimelineNote';

interface Props {
  container: DayPlanContainerRow;
  drop: DayPlanDropRow;
  index: number;
  runDate: string;
  recipientTimezone: string | null;
  /** Clears the slot's tombstone/pin — shown only on human-owned rows. */
  onResetSlot?: (slotId: string) => void;
  /** Explicitly skips this slot's drop today (deletes the draft). */
  onSkipSlot?: (slotId: string) => void;
  /** Turns the slot OFF in container config — gone from every day. */
  onDisableSlot?: (containerId: string, slotId: string) => void;
}

/** Nothing to say about the drop: the notes column shows a dash. */
const quiet = (d: DayPlanDropRow) =>
  !d.clamped && !d.exceeds24h && !d.optimizer && !d.hitAgain && !d.outsideWindow && !d.listFirstDay && !d.scaled && !d.returnTest && !d.weekend && !d.experiment && !d.hourNote && !d.runtime?.tombstoned && !d.runtime?.pinned && !d.runtime?.sentAt && !(d.runtime && !d.runtime.hasDraft);

export const TimelineRow: FC<Props> = ({ container, drop, index, runDate, recipientTimezone, ...actions }) => (
  <div className={cn(`${TIMELINE_GRID} group border-b border-border/30 py-1.5 text-sm last:border-b-0`, (drop.clamped || drop.runtime?.tombstoned) && 'bg-warning/5')}>
    <span className="h-full min-h-[24px] w-1 rounded-sm" style={{ backgroundColor: spineColor(container.color, true) }} />
    <TimeCell drop={drop} runDate={runDate} recipientTimezone={recipientTimezone} />
    <span className="font-mono text-xs text-muted-foreground">{index + 1}</span>
    <ListCell container={container} drop={drop} />
    <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <TimelineNote drop={drop} />
      <SlotActions containerId={container.containerId} drop={drop} {...actions} />
      {quiet(drop) && !drop.capacityUnknown ? '—' : null}
    </span>
    <GatewayCell drop={drop} />
    <SendersCell drop={drop} />
    <TextsCell drop={drop} />
    <span className="text-right font-mono text-xs tabular-nums">{drop.amount.toLocaleString('en-US')}</span>
    <span className="text-right font-mono text-xs tabular-nums text-muted-foreground" data-testid="drop-price" title={drop.pricePerSms != null ? 'the route’s price per SMS' : 'no price on this route'}>
      {drop.pricePerSms != null ? usd(drop.pricePerSms, 3) : '—'}
    </span>
    <span className="text-right font-mono text-xs tabular-nums text-muted-foreground">{drop.estCost !== null ? usd(drop.estCost, 0) : '—'}</span>
  </div>
);
